import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Clone, ContactShadows, Edges, Line, useGLTF, useTexture } from "@react-three/drei";
import * as THREE from "three";
import { AnimatePresence, motion } from "framer-motion";

const WALK_Y = 1.6;
const BOUNDS = { x: 4.2, zMin: -5.2, zMax: 5.4 };

const HOTSPOTS = [
  { name: "shelves", label: "Hat Shelves", pos: [-2.6, -0.4], look: [-4.62, 1.55, -0.4] },
  { name: "rack", label: "Shirt Rack", pos: [-2.8, -3.5], look: [-4.72, 1.25, -3.5] },
  { name: "counter", label: "Checkout", pos: [0.5, -1.9], look: [0.5, 1.0, -4.35] },
  { name: "art", label: "Art Wall", pos: [2.9, -2.6], look: [4.94, 1.9, -2.6] },
];

const usePreparedTexture = (url) => {
  const tex = useTexture(url);
  useEffect(() => {
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 16;
  }, [tex]);
  return tex;
};

const CapModel = ({ position, rotationY = 0, product, register }) => {
  const { scene } = useGLTF("/models/base.glb");
  return (
    <group
      position={position}
      rotation={[0, rotationY, 0]}
      userData={product ? { product } : undefined}
      ref={(o) => {
        if (o && product && register) register(o);
      }}
    >
      <Clone object={scene} scale={0.27} position={[0, 0.02, -0.05]} />
    </group>
  );
};

const ShirtModel = ({ position, tilt = 0, product, register }) => {
  const { scene } = useGLTF("/models/shirt.glb");
  const floatRef = useRef();
  useFrame(({ clock }) => {
    if (floatRef.current) {
      floatRef.current.position.y = position[1] + Math.sin(clock.elapsedTime * 1.2) * 0.025;
    }
  });
  return (
    <group
      position={position}
      rotation={[0, Math.PI / 2 + tilt, 0]}
      userData={product ? { product } : undefined}
      ref={(o) => {
        floatRef.current = o;
        if (o && product && register) register(o);
      }}
    >
      <Clone object={scene} scale={0.35} />
    </group>
  );
};

const RugModel = () => {
  const { scene } = useGLTF("/models/rug.glb");
  return (
    <Clone object={scene} scale={3.2} rotation={[-Math.PI / 2, 0, 0]} position={[1.1, 0.005, 2.85]} />
  );
};

const GlowLights = ({ glowRef }) => {
  const amb = useRef();
  const dir = useRef();
  useFrame((_, delta) => {
    const lum = glowRef.current.lum;
    const k = 0.8 + lum * 0.6;
    if (amb.current) amb.current.intensity = THREE.MathUtils.damp(amb.current.intensity, 1.05 * k, 4, delta);
    if (dir.current) dir.current.intensity = THREE.MathUtils.damp(dir.current.intensity, 1.1 * k, 4, delta);
  });
  return (
    <>
      <ambientLight ref={amb} intensity={1.05} />
      <directionalLight ref={dir} position={[3, 6, 4]} intensity={1.1} />
      <directionalLight position={[-4, 5, -2]} intensity={0.4} />
    </>
  );
};

const CeilingVideo = ({ glowRef }) => {
  const video = useMemo(() => {
    const v = document.createElement("video");
    const s1 = document.createElement("source");
    s1.src = "/videos/killbill.webm";
    s1.type = "video/webm";
    const s2 = document.createElement("source");
    s2.src = "/videos/killbill.mp4";
    s2.type = "video/mp4";
    v.appendChild(s1);
    v.appendChild(s2);
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.preload = "auto";
    v.play().catch(() => {});
    window.__vid = v;
    return v;
  }, []);

  const tex = useMemo(() => {
    const t = new THREE.VideoTexture(video);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 16;
    t.center.set(0.5, 0.5);
    t.rotation = Math.PI / 2;
    return t;
  }, [video]);

  useEffect(() => {
    const applyCover = () => {
      const va = video.videoWidth / video.videoHeight;
      const pa = 12 / 10;
      if (va > pa) {
        tex.repeat.set(pa / va, 1);
        tex.offset.set((1 - pa / va) / 2, 0);
      } else {
        tex.repeat.set(1, va / pa);
        tex.offset.set(0, (1 - va / pa) / 2);
      }
    };
    if (video.readyState >= 1) applyCover();
    else video.addEventListener("loadedmetadata", applyCover, { once: true });
    const start = () => video.play().catch(() => {});
    const relloop = () => {
      video.currentTime = 0;
      video.play().catch(() => {});
    };
    video.addEventListener("ended", relloop);
    window.addEventListener("pointerdown", start, { once: true });

    const sampler = document.createElement("canvas");
    sampler.width = 16;
    sampler.height = 16;
    const sctx = sampler.getContext("2d", { willReadFrequently: true });
    const sample = setInterval(() => {
      if (video.readyState < 2 || video.paused) return;
      try {
        sctx.drawImage(video, 0, 0, 16, 16);
        const d = sctx.getImageData(0, 0, 16, 16).data;
        let sum = 0;
        for (let i = 0; i < d.length; i += 4) {
          sum += d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
        }
        glowRef.current.lum = sum / (d.length / 4) / 255;
        window.__glowLum = glowRef.current.lum;
      } catch {
        /* frame not ready */
      }
    }, 200);

    return () => {
      window.removeEventListener("pointerdown", start);
      video.removeEventListener("ended", relloop);
      clearInterval(sample);
    };
  }, [video, tex]);

  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: { map: { value: tex } },
        vertexShader:
          "varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }",
        fragmentShader:
          "uniform sampler2D map; varying vec2 vUv; void main(){ vec4 c = texture2D(map, vUv); float g = dot(c.rgb, vec3(0.299, 0.587, 0.114)); gl_FragColor = vec4(vec3(g * 1.1), 1.0); }",
      }),
    [tex]
  );

  return (
    <mesh rotation-x={Math.PI / 2} position={[0, 4, 0]} material={material}>
      <planeGeometry args={[10, 12]} />
    </mesh>
  );
};

const RoomShell = ({ glowRef }) => (
  <group>
    <mesh rotation-x={-Math.PI / 2} position={[0, 0, 0]}>
      <planeGeometry args={[10, 12]} />
      <meshStandardMaterial color="#fafafa" />
    </mesh>
    <mesh position={[0, 2, -6]}>
      <planeGeometry args={[10, 4]} />
      <meshStandardMaterial color="#ffffff" />
    </mesh>
    <mesh rotation-y={Math.PI / 2} position={[-5, 2, 0]}>
      <planeGeometry args={[12, 4]} />
      <meshStandardMaterial color="#ffffff" />
    </mesh>
    <mesh rotation-y={-Math.PI / 2} position={[5, 2, 0]}>
      <planeGeometry args={[12, 4]} />
      <meshStandardMaterial color="#ffffff" />
    </mesh>
    <CeilingVideo glowRef={glowRef} />
    <Line points={[[-5, 0, -6], [-5, 4, -6]]} color="#111" lineWidth={1} />
    <Line points={[[5, 0, -6], [5, 4, -6]]} color="#111" lineWidth={1} />
    <Line points={[[-5, 4, -6], [5, 4, -6]]} color="#111" lineWidth={1} />
    <Line points={[[-5, 4, -6], [-5, 4, 6]]} color="#111" lineWidth={1} />
    <Line points={[[5, 4, -6], [5, 4, 6]]} color="#111" lineWidth={1} />
    <Line points={[[-5, 0, -6], [5, 0, -6]]} color="#111" lineWidth={1} />
  </group>
);

const BackWallDecor = ({ logo }) => (
  <group>
    <mesh position={[0.6, 2.25, -5.95]}>
      <planeGeometry args={[3.2, 0.66]} />
      <meshStandardMaterial map={logo} transparent alphaTest={0.1} />
    </mesh>
    <mesh position={[-2.4, 1.05, -5.93]}>
      <boxGeometry args={[1.05, 2.1, 0.1]} />
      <meshStandardMaterial color="#050505" />
    </mesh>
  </group>
);

const Counter = ({ register }) => (
  <group
    position={[0.5, 0, -4.35]}
    userData={{ product: "CHECKOUT" }}
    ref={(o) => {
      if (o && register) register(o);
    }}
  >
    <mesh position={[0, 0.45, 0]}>
      <boxGeometry args={[1.9, 0.9, 0.75]} />
      <meshStandardMaterial color="#b8b8b8" />
    </mesh>
    {[-0.99, 0.99].map((x) => (
      <mesh key={x} position={[x, 0.49, 0]}>
        <boxGeometry args={[0.09, 0.98, 0.83]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
    ))}
    <mesh position={[0, 0.94, 0]}>
      <boxGeometry args={[2.07, 0.04, 0.85]} />
      <meshPhysicalMaterial color="#ffffff" transparent opacity={0.35} />
    </mesh>
  </group>
);

const ARTWORKS = [
  { url: "/images/art-4f.webp", z: -4.6 },
  { url: "/images/art-2f.webp", z: -2.6 },
  { url: "/images/art-3f.webp", z: -0.6 },
];

const ArtPiece = ({ url, z }) => {
  const tex = usePreparedTexture(url);
  return (
    <group position={[4.94, 2.0, z]} rotation-y={-Math.PI / 2}>
      <mesh position={[0, 0, -0.03]}>
        <boxGeometry args={[1.35, 1.95, 0.06]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
      <mesh position={[0, 0, 0.008]}>
        <planeGeometry args={[1.2, 1.8]} />
        <meshBasicMaterial map={tex} polygonOffset polygonOffsetFactor={-1} />
      </mesh>
    </group>
  );
};

const Paintings = () => (
  <group>
    {ARTWORKS.map((a) => (
      <ArtPiece key={a.url} {...a} />
    ))}
  </group>
);

const Rack = ({ register }) => (
  <ShirtModel position={[-4.7, 1.05, -3.5]} product="VISION TEE" register={register} />
);

const SHELF_CAPS = ["V DAISY CAP", "TRIPLE DAISY CAP", "BLOOM CAP"];

const Shelves = ({ register }) => (
  <group>
    {[0.95, 1.6, 2.25].map((y, i) => (
      <group key={y} position={[-4.62, y, -0.4]}>
        <mesh>
          <boxGeometry args={[0.8, 0.05, 0.42]} />
          <meshStandardMaterial color="#f2f2f2" />
          <Edges color="#111" />
        </mesh>
        <CapModel
          position={[0, 0.03, 0]}
          rotationY={Math.PI / 2 + (i - 1) * 0.15}
          product={SHELF_CAPS[i]}
          register={register}
        />
      </group>
    ))}
  </group>
);

const Walker = ({ targetRef, dragRef, clickablesRef, onProduct, onHotspot }) => {
  const { camera, gl } = useThree();
  const look = useRef({ yaw: 0, pitch: -0.06 });
  const face = useRef({ yaw: 0, pitch: -0.06, active: false });
  const ring = useRef();

  useEffect(() => {
    window.__cam = camera;
    camera.rotation.order = "YXZ";
    camera.rotation.set(look.current.pitch, look.current.yaw, 0);
    const el = gl.domElement;
    el.style.cursor = "grab";
    let down = false;
    let lastX = 0;
    let lastY = 0;
    let total = 0;

    const raycastProducts = (clientX, clientY) => {
      const rect = el.getBoundingClientRect();
      const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -((clientY - rect.top) / rect.height) * 2 + 1;
      const ray = new THREE.Raycaster();
      ray.setFromCamera(new THREE.Vector2(nx, ny), camera);
      const hits = ray.intersectObjects(clickablesRef.current, true);
      if (!hits.length) return null;
      let o = hits[0].object;
      while (o && !o.userData.product) o = o.parent;
      return o ? o.userData.product : null;
    };

    const onDown = (e) => {
      down = true;
      total = 0;
      lastX = e.clientX;
      lastY = e.clientY;
      dragRef.current = false;
      el.style.cursor = "grabbing";
    };
    const onMove = (e) => {
      if (down) {
        const dx = e.clientX - lastX;
        const dy = e.clientY - lastY;
        lastX = e.clientX;
        lastY = e.clientY;
        total += Math.abs(dx) + Math.abs(dy);
        if (total > 8) dragRef.current = true;
        face.current.active = false;
        look.current.yaw += dx * 0.0032;
        look.current.pitch = THREE.MathUtils.clamp(look.current.pitch + dy * 0.0032, -1.15, 1.15);
        return;
      }
      el.style.cursor = raycastProducts(e.clientX, e.clientY) ? "pointer" : "grab";
    };
    const onUp = () => {
      down = false;
      el.style.cursor = "grab";
      setTimeout(() => {
        dragRef.current = false;
      }, 60);
    };
    const onClick = (e) => {
      if (dragRef.current) return;

      const product = raycastProducts(e.clientX, e.clientY);
      if (product) {
        onProduct(product);
        return;
      }

      const rect = el.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      const origin = new THREE.Vector3(nx, ny, 0.5).unproject(camera);
      const dir = origin.sub(camera.position).normalize();
      if (dir.y >= -0.005) dir.y = -0.005;
      const t = -camera.position.y / dir.y;
      const x = THREE.MathUtils.clamp(camera.position.x + dir.x * t, -BOUNDS.x, BOUNDS.x);
      const z = THREE.MathUtils.clamp(camera.position.z + dir.z * t, BOUNDS.zMin, BOUNDS.zMax);

      let best = HOTSPOTS[0];
      let bestDist = Infinity;
      for (const h of HOTSPOTS) {
        const d = (h.pos[0] - x) ** 2 + (h.pos[1] - z) ** 2;
        if (d < bestDist) {
          bestDist = d;
          best = h;
        }
      }

      onHotspot(best.label);
      targetRef.current.set(best.pos[0], WALK_Y, best.pos[1]);
      const [lx, ly, lz] = best.look;
      face.current.yaw = Math.atan2(-(lx - best.pos[0]), -(lz - best.pos[1]));
      face.current.pitch = Math.atan2(
        ly - WALK_Y,
        Math.hypot(lx - best.pos[0], lz - best.pos[1])
      );
      face.current.active = true;
    };

    el.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    el.addEventListener("click", onClick);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      el.removeEventListener("click", onClick);
    };
  }, [camera, gl, dragRef, targetRef, clickablesRef, onProduct, onHotspot]);

  useFrame((state, delta) => {
    const t = targetRef.current;
    camera.position.x = THREE.MathUtils.damp(camera.position.x, t.x, 3.2, delta);
    camera.position.z = THREE.MathUtils.damp(camera.position.z, t.z, 3.2, delta);
    camera.position.y = WALK_Y;

    if (face.current.active) {
      const dyaw = Math.atan2(
        Math.sin(face.current.yaw - look.current.yaw),
        Math.cos(face.current.yaw - look.current.yaw)
      );
      look.current.yaw += dyaw * (1 - Math.exp(-3.2 * delta));
      look.current.pitch = THREE.MathUtils.damp(look.current.pitch, face.current.pitch, 3.2, delta);
      if (Math.abs(dyaw) < 0.01) face.current.active = false;
    }
    camera.rotation.y = look.current.yaw;
    camera.rotation.x = look.current.pitch;

    if (ring.current) {
      ring.current.position.set(t.x, 0.03, t.z);
      const s = 1 + Math.sin(state.clock.elapsedTime * 4) * 0.1;
      ring.current.scale.set(s, s, s);
    }
  });

  return (
    <mesh ref={ring} rotation-x={-Math.PI / 2}>
      <ringGeometry args={[0.18, 0.24, 32]} />
      <meshBasicMaterial color="#111111" transparent opacity={0.55} />
    </mesh>
  );
};

const SceneContent = ({ targetRef, dragRef, clickablesRef, onProduct, onHotspot }) => {
  const logo = usePreparedTexture("/images/vison-logo.webp");
  const glowRef = useRef({ lum: 0.5 });

  const register = (obj) => {
    if (!clickablesRef.current.includes(obj)) clickablesRef.current.push(obj);
  };

  return (
    <>
      <GlowLights glowRef={glowRef} />
      <RoomShell glowRef={glowRef} />
      <RugModel />
      <BackWallDecor logo={logo} />
      <Counter register={register} />
      <Paintings />
      <Rack register={register} />
      <Shelves register={register} />
      <ContactShadows position={[0, 0.01, 0]} opacity={0.28} scale={12} blur={2.4} far={2.5} resolution={512} />
      <Walker
        targetRef={targetRef}
        dragRef={dragRef}
        clickablesRef={clickablesRef}
        onProduct={onProduct}
        onHotspot={onHotspot}
      />
    </>
  );
};

const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [
      { freq: 987.77, at: 0, dur: 0.35 },
      { freq: 1318.51, at: 0.09, dur: 0.5 },
    ];
    notes.forEach(({ freq, at, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + dur + 0.05);
    });
    osc_cleanup(ctx);
  } catch {
    /* audio unsupported */
  }
};

const osc_cleanup = (ctx) => setTimeout(() => ctx.close(), 1200);

export default function VirtualStore() {
  const navigate = useNavigate();
  const targetRef = useRef(new THREE.Vector3(0, WALK_Y, 4.6));
  const dragRef = useRef(false);
  const clickablesRef = useRef([]);
  const [entered, setEntered] = useState(false);
  const [hotspotLabel, setHotspotLabel] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 1800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!hotspotLabel) return;
    const t = setTimeout(() => setHotspotLabel(null), 4500);
    return () => clearTimeout(t);
  }, [hotspotLabel]);

  return (
    <motion.div
      data-testid="virtual-store"
      className="fixed inset-0 bg-white"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div data-lenis-prevent className="absolute inset-0">
        <Canvas
          dpr={[1, 2]}
          camera={{ fov: 62, position: [0, WALK_Y, 4.6], near: 0.1, far: 60 }}
          gl={{ antialias: true }}
        >
          <Suspense fallback={null}>
            <SceneContent
              targetRef={targetRef}
              dragRef={dragRef}
              clickablesRef={clickablesRef}
              onProduct={(product) => {
                if (product === "CHECKOUT") {
                  playChime();
                  navigate("/preorder");
                } else if (product === "VISION TEE") {
                  navigate("/t-shirts");
                } else {
                  navigate("/accessories");
                }
              }}
              onHotspot={setHotspotLabel}
            />
          </Suspense>
        </Canvas>
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-4 flex justify-center px-4 sm:top-5">
        <span
          data-testid="walk-hint"
          className="rounded-full border border-neutral-200 bg-white/80 px-3 py-1.5 text-center text-[9px] uppercase tracking-[0.18em] text-neutral-500 backdrop-blur-sm sm:px-4 sm:text-xs sm:tracking-[0.3em]"
        >
          <span className="sm:hidden">Drag to look · Tap to visit</span>
          <span className="hidden sm:inline">Drag to look · Click to visit · Tap items to shop</span>
        </span>
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-14 flex justify-center sm:top-16">
        <AnimatePresence>
          {hotspotLabel && (
            <motion.span
              key={hotspotLabel}
              data-testid="hotspot-label"
              className="rounded-full bg-neutral-900 px-3.5 py-1.5 text-[9px] uppercase tracking-[0.25em] text-white sm:px-4 sm:text-xs sm:tracking-[0.35em]"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              {hotspotLabel}
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <div className="absolute inset-x-0 bottom-7 flex justify-center sm:bottom-10">
        <button
          type="button"
          data-testid="shop-now-link"
          onClick={() => navigate("/home")}
          className="text-[10px] font-normal uppercase tracking-[0.3em] text-neutral-900 underline underline-offset-8 transition-colors hover:text-neutral-400 sm:text-xs sm:tracking-[0.4em]"
        >
          Shop now
        </button>
      </div>

      <AnimatePresence>
        {!entered && (
          <motion.div
            data-testid="store-entrance"
            className="absolute inset-0 z-50 flex items-center justify-center bg-black"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: "easeInOut" }}
          >
            <motion.img
              src="/images/vison-logo.webp"
              alt="VISON"
              className="w-44 invert sm:w-56"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              draggable="false"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
